import store from '@state/store'

export default [
  {
    path: '/',
    name: 'home',
    component: () => lazyLoadView(import('@views/landing')),
  },
  {
    path: '/:site/login',
    name: 'login',
    component: () => lazyLoadView(import('@views/login')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the profile page instead
          next({
            name: 'profile',
            params: {
              site: routeTo.params.site,
            },
          })
        } else {
          next()
        }
      },
    },
  },

  {
    path: '/:site/search/:slug',
    name: 'search-results',
    component: () => lazyLoadView(import('@views/search-results')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
  },

  {
    // path: '/:site/product/:itemId',
    path: '/:site/:categorySlug/:itemSlug',
    name: 'product-page',
    component: () => lazyLoadView(import('@views/product')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
  },

  {
    path: '/:site/categories',
    name: 'categories',
    component: () => lazyLoadView(import('@views/categories')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },

  {
    path: '/:site/signup',
    name: 'signup',
    component: () => lazyLoadView(import('@views/sign-up')),
    // Navigate to profile page if logged in
    meta: {
      needSite: true,
      usePathAnalytics: true,
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({
            name: 'profile',
            params: {
              site: routeTo.params.site,
            },
          })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/:site/profile',
    name: 'profile',
    component: () => lazyLoadView(import('@views/profile')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
      beforeResolve(routeTo, routeFrom, next) {
        // If not logged in, route to login
        if (store.getters['auth/loggedIn']) {
          next()
        } else {
          next({
            name: 'login',
            params: {
              site: routeTo.params.site,
            },
          })
        }
      },
    },
    // Set the user from the route params, once it's set in the
    // beforeResolve route guard.
    props: (route) => ({
      user: route.params.user,
    }),
  },
  {
    path: '/:site',
    name: 'PublicSiteHome',
    component: () => lazyLoadView(import('@views/home')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },
  {
    path: '/:site/cart',
    name: 'cart',
    component: () => lazyLoadView(import('@views/cart')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },
  {
    path: '/:site/checkout',
    name: 'checkout',
    component: () => lazyLoadView(import('@views/checkout')),
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
    meta: {
      needSite: true,
      usePathAnalytics: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Nav to cart page unless cart has at least one item
        const cart = store.getters['shoppingCart/getShoppingCart']

        if (cart.cookie) {
          if (cart.items.length > 0) {
            next()
          } else {
            next({
              name: 'cart',
              params: {
                site: routeTo.params.site,
              },
            })
          }
        } else {
          return new Promise((resolve, reject) => {
            store.dispatch('shoppingCart/fetchShoppingCart').then((cart) => {
              if (cart.items.length > 0) {
                next()
              } else {
                next({
                  name: 'cart',
                  params: {
                    site: routeTo.params.site,
                  },
                })
              }
            })
          })
        }
      },
    },
  },
  {
    path: '/:site/order-summary',
    name: 'order-summary',
    component: () => lazyLoadView(import('@views/order-summary')),
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
    meta: {
      needSite: true,
      usePathAnalytics: true,
      beforeResolve(routeTo, routeFrom, next) {
        // Require that routeFrom is checkout, and there is an order present in the order module
        // Otherwise navigate to cart.
        const orderPlaced = !!store.getters['orders/getOrder']
        const fromCheckout = routeFrom.name === 'checkout'
        if (orderPlaced && fromCheckout) {
          next()
        } else {
          next({
            name: 'cart',
            params: {
              site: routeTo.params.site,
            },
          })
        }
      },
    },
  },

  {
    path: '/:site/catering',
    name: 'catering',
    component: () => lazyLoadView(import('@views/catering')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },

  {
    path: '/:site/contact',
    name: 'contact',
    component: () => lazyLoadView(import('@views/contact')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },

  {
    path: '/:site/how-it-works',
    name: 'how-it-works',
    component: () => lazyLoadView(import('@views/how-it-works')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },

  {
    path: '/:site/locations',
    name: 'locations',
    component: () => lazyLoadView(import('@views/locations')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },

  // {
  //   path: '/:site/mingle-nosh',
  //   name: 'mingle-and-nosh',
  //   component: () => lazyLoadView(import('@views/mingle-and-nosh')),
  //   meta: {
  //     needSite: true,
  //     usePathAnalytics: true,
  //   },
  //   props: (route) => ({
  //     user: store.state.auth.currentUser || {},
  //   }),
  // },

  {
    path: '/:site/parents',
    name: 'parents',
    component: () => lazyLoadView(import('@views/parents')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },

  {
    path: '/:site/privacy-policy',
    name: 'privacy-policy',
    component: () => lazyLoadView(import('@views/privacy-policy')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },

  {
    path: '/:site/refund-policy',
    name: 'refund-policy',
    component: () => lazyLoadView(import('@views/refund-policy')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },

  {
    path: '/:site/terms-of-use',
    name: 'terms-of-use',
    component: () => lazyLoadView(import('@views/terms-of-use')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },

  {
    // Matches /reset_password/:public_key
    path: '/:site/reset_password/:public_key',
    name: 'CustomerPasswordReset',
    component: () => lazyLoadView(import('@views/password-reset')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
  },
  {
    path: '/:site',
    name: 'PreLaunch',
    component: () => lazyLoadView(import('@views/prelaunch')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    props: (route) => ({
      user: store.state.auth.currentUser || {},
    }),
  },

  {
    path: '/:site/:categorySlug',
    name: 'category-page',
    component: () => lazyLoadView(import('@views/category')),
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
  },

  {
    path: '/404',
    name: 'NotFoundPage',
    component: require('@views/_404').default,
    meta: {
      needSite: true,
      usePathAnalytics: true,
    },
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '/404',
  },
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/_timeout').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}
